import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "template",
    "fields",
    "ingredientsTemplate",
    "recipesTemplate",
    "newIngredientTemplate",
    "ingredientRowTemplate",
    "subRecipesHeader",
    "ingredientsSeparator",
  ];

  connect() {
    this.wrapperClass = this.data.get("wrapperClass") || "nested-fields";
    this.newIngredients = [];

    // Écouter l'événement de remplacement
    this.element.addEventListener(
      "replace-with-recipe",
      this.replaceWithRecipe.bind(this)
    );

    // Add keyboard shortcut for adding ingredient rows (Ctrl+Enter)
    this.element.addEventListener(
      "keydown",
      this.handleKeyboardShortcuts.bind(this)
    );
  }

  add(event) {
    event.preventDefault();
    const type = event.params.type || "ingredient";

    let template;
    let rowsToAdd = 1;
    let insertLocation = this.fieldsTarget;
    let insertMethod = "beforeend";

    switch (type) {
      case "recipe":
        template = this.recipesTemplateTarget.innerHTML;
        break;
      case "newIngredient":
        template = this.newIngredientTemplateTarget.innerHTML;
        break;
      case "ingredient-row":
        template = this.ingredientRowTemplateTarget.innerHTML;
        rowsToAdd = 1; // Add 1 row at a time for better UX
        // Insert ingredient rows BEFORE the ingredients separator
        if (this.hasIngredientsSeparatorTarget) {
          insertLocation = this.ingredientsSeparatorTarget;
          insertMethod = "beforebegin";
        }
        break;
      default:
        template = this.ingredientsTemplateTarget.innerHTML;
    }

    // Add multiple rows if specified
    for (let i = 0; i < rowsToAdd; i++) {
      const timestamp = new Date().getTime() + i; // Ensure unique timestamps
      const content = template.replace(/NEW_RECORD/g, timestamp);
      insertLocation.insertAdjacentHTML(insertMethod, content);

      // Track new ingredients to potentially update dropdowns later
      if (type === "newIngredient") {
        this.newIngredients.push(timestamp);
      }
    }

    // Focus on the first new row's ingredient name field
    if (type === "ingredient-row") {
      setTimeout(() => {
        const newRows = this.fieldsTarget.querySelectorAll(
          '[data-ingredients-table-spreadsheet-target="ingredientNameInput"]:not([disabled])'
        );
        const lastRow = newRows[newRows.length - rowsToAdd];
        if (lastRow) {
          lastRow.focus();
        }
      }, 50);
    }

    // Trigger cost calculator update when sub-recipes are added
    if (type === "recipe" || type === "ingredient") {
      this.triggerCostCalculatorUpdate();
    }

    // Show sub-recipes header when a sub-recipe is added
    if (type === "recipe") {
      this.updateSubRecipesHeaderVisibility();
    }
  }

  handleKeyboardShortcuts(event) {
    // Ctrl+Enter to add ingredient rows
    if (event.ctrlKey && event.key === "Enter") {
      event.preventDefault();
      const addButton = this.element.querySelector(
        '[data-nested-form-type-param="ingredient-row"]'
      );
      if (addButton) {
        addButton.click();
      }
    }
  }

  remove(event) {
    event.preventDefault();
    const wrapper = event.target.closest(`.${this.wrapperClass}`);

    if (wrapper.dataset.newRecord === "true") {
      wrapper.remove();
    } else {
      wrapper.style.display = "none";
      wrapper.querySelector("input[name*='_destroy']").value = "1";
    }

    // Trigger cost calculator update when sub-recipes are removed
    this.triggerCostCalculatorUpdate();

    // Update header visibility when sub-recipes are removed
    this.updateSubRecipesHeaderVisibility();
  }

  async replaceWithRecipe(event) {
    const { recipeId } = event.detail;
    const ingredientWrapper = event.target.closest(`.${this.wrapperClass}`);

    // On marque l'ingrédient comme supprimé
    if (ingredientWrapper.dataset.newRecord !== "true") {
      ingredientWrapper.querySelector("input[name*='_destroy']").value = "1";
    }
    ingredientWrapper.style.display = "none";

    // On ajoute la sous-recette
    const content = this.recipesTemplateTarget.innerHTML
      .replace(/NEW_RECORD/g, new Date().getTime())
      .replace(/RECIPE_ID/g, recipeId);

    this.fieldsTarget.insertAdjacentHTML("beforeend", content);
  }

  updateSubRecipesHeaderVisibility() {
    if (!this.hasSubRecipesHeaderTarget) return;

    // Check if there are visible sub-recipe rows
    const visibleSubRecipes = this.element.querySelectorAll(
      '[data-cost-calculator-target="subRecipeRow"]:not([style*="display: none"])'
    );

    if (visibleSubRecipes.length > 0) {
      this.subRecipesHeaderTarget.classList.remove("hidden");
    } else {
      this.subRecipesHeaderTarget.classList.add("hidden");
    }
  }

  triggerCostCalculatorUpdate() {
    // Find cost calculator controller and trigger recalculation with a small delay
    // to ensure DOM has been updated
    setTimeout(() => {
      const costCalculatorController =
        this.application.getControllerForElementAndIdentifier(
          this.element,
          "cost-calculator"
        );

      if (costCalculatorController) {
        console.log("Triggering cost calculator recalculation...");
        costCalculatorController.recalculate();
      } else {
        console.warn("Cost calculator controller not found");
      }
    }, 100);
  }
}
